if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.tabs = this.querySelectorAll(".filter-tab");
      this.panels = this.querySelectorAll(".filter-tab-panel");
      this.clearButton = this.querySelector(".filter-clear");

      this.setupEventListeners();


      if (!this.select && !this.isVehicleManagedPanel(this.activePanel)) {
        this.populateCollectionFilters(true);
      }
    }

    isVehicleManagedPanel(panel) {
      // All three tabs are managed by dedicated Convermax scripts
      // (section-vehicle-filter.js + section-size-filter.js), so the stock
      // collection-filtering logic should not populate or submit them.
      return (
        !!panel &&
        (panel.id.endsWith("-vehicle") ||
          panel.id.endsWith("-size") ||
          panel.id.endsWith("-part_number"))
      );
    }

    get activePanel() {
      return this.querySelector(".filter-tab-panel.is-active") || this.querySelector(".filter-tab-panel");
    }

    activeSelects() {
      const panel = this.activePanel;
      return panel ? panel.querySelectorAll("select.dropdown-filter") : this.querySelectorAll("select.dropdown-filter");
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();

        if (this.isVehicleManagedPanel(this.activePanel)) {
          return;
        }

        window.location = this.form.getAttribute("action");
      });
      this.select?.addEventListener("change", this.onCollectionChange.bind(this));

      this.panels.forEach((panel) => {
        if (this.isVehicleManagedPanel(panel)) {
          return;
        }

        panel.querySelectorAll("select.dropdown-filter").forEach((select) => {
          select.addEventListener("change", this.onFilterChange.bind(this));
        });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick.bind(this));
      });

      this.clearButton?.addEventListener("click", this.onClear.bind(this));
    }

    // Reset the currently active tab's inputs. On a results page (has search
    // params) also navigate to drop the applied search; on the homepage it is
    // an in-place reset. Shared by the Vehicle / Size / Part tabs of both the
    // homepage and header widgets.
    onClear(event) {
      if (event) event.preventDefault();

      const panel = this.activePanel;
      let clearedVehicle = false;
      if (panel) {
        const isVehicle = panel.id.endsWith("-vehicle");
        // Size fields no longer depend on each other (Akshay 10-20 Issue 6), so they stay open.
        const isSize = panel.id.endsWith("-size");

        panel.querySelectorAll("select.dropdown-filter").forEach((select) => {
          if (Number(select.dataset.index) > 1 && !isSize) {
            select
              .querySelectorAll("option:not(.default)")
              .forEach((option) => option.remove());
            select.disabled = true;
            select.closest(".form-group")?.classList.add("disabled");
          }
          const def = select.querySelector("option.default") || select.options[0];
          if (def) def.selected = true;
          select.value = "";
        });

        if (isSize) panel.dispatchEvent(new CustomEvent("cm:size-cleared"));

        const input = panel.querySelector("input.part-number-input");
        if (input) input.value = "";

        if (this.button) this.button.disabled = true;

        if (isVehicle) {
          clearedVehicle = true;
          try {
            localStorage.removeItem("cm_selected_vehicle");
          } catch (e) {}
          if (window.Convermax && typeof window.Convermax.discardVehicle === "function") {
            try {
              window.Convermax.discardVehicle();
            } catch (e) {}
          }
        }
      }

      if (clearedVehicle) {
        const onProduct = document.body.classList.contains("template-product");
        if (onProduct) this.stripFitmentFromLocation();
        document.dispatchEvent(new CustomEvent("cm:vehicle-cleared"));
        /* A full navigation would leave the title ribbon up until the next
           document. Drop only the fitment query and let the page update. */
        if (onProduct) return;
      }

      // On a results page, clear the applied search too.
      if (window.location.search && window.location.search.length > 1) {
        window.location = window.location.pathname;
      }
    }

    stripFitmentFromLocation() {
      const raw = window.location.search.replace(/^\?/, "");
      if (!raw) return;
      const kept = [];
      raw.split("&").forEach((part) => {
        let decoded = part;
        try {
          decoded = decodeURIComponent(part);
        } catch (e) {}
        const segs = decoded.split("/");
        const hasFitment = segs.some(
          (seg) => seg === "Year" || seg === "Make" || seg === "Model" || seg === "Fitment",
        );
        if (!hasFitment) {
          kept.push(part);
          return;
        }
        const out = [];
        for (let i = 0; i < segs.length; i++) {
          const key = segs[i];
          if (key === "Year" || key === "Make" || key === "Model") {
            i += 1;
            continue;
          }
          if (key === "Fitment" || key === "Vehicle-Specific") continue;
          if (key) out.push(encodeURIComponent(key));
        }
        if (out.length) kept.push(out.join("/"));
      });
      const next = kept.length ? `?${kept.join("&")}` : "";
      window.history.replaceState(
        {},
        "",
        window.location.pathname + next + (window.location.hash || ""),
      );
    }

    onTabClick(event) {
      const button = event.currentTarget;

      if (button.classList.contains("is-active")) {
        return;
      }

      const targetPanel = this.querySelector(`#${button.dataset.tabTarget}`);

      if (!targetPanel) {
        return;
      }

      this.tabs.forEach((tab) => {
        tab.classList.remove("is-active");
        tab.setAttribute("aria-selected", "false");
      });
      this.panels.forEach((panel) => {
        panel.classList.remove("is-active");
        panel.setAttribute("hidden", "");
      });

      button.classList.add("is-active");
      button.setAttribute("aria-selected", "true");
      targetPanel.classList.add("is-active");
      targetPanel.removeAttribute("hidden");

      if (this.isVehicleManagedPanel(targetPanel)) {
        return;
      }

      if (targetPanel.dataset.populated === "true") {
        this.setFormUrl(this.constructURL());
        this.button.disabled = this.activeSelects().length === 0;
      } else {
        this.populateCollectionFilters(false, targetPanel);
      }
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url;
      const url = new URL(collectionUrl, window.requestShopUrl);

      this.activeSelects().forEach((select) => {
        if (select.disabled || select.value === "") {
          return;
        }

        Array.from(select.selectedOptions).forEach((option) => {
          url.searchParams.set(option.dataset.paramName, option.value);
        });
      });

      return url.toString();
    }

    setFormUrl(url, background) {
      this.form.setAttribute("action", url);

      // This operation happens on the background
      // where the user should not be notified of loading with spinners
      // etc.
      if (!background) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector("option").selected = true;
    }

    onFilterChange(event) {
      const selectedIndex = event.target.dataset.index;
      const panel = event.target.closest(".filter-tab-panel") || this;
      const selects = panel.querySelectorAll("select.dropdown-filter");

      selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.setFormUrl(this.constructURL());

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      this.toggleLoading();

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          this.toggleLoading();
          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          selects.forEach((select) => {
            if (Number(select.dataset.index) <= Number(selectedIndex)) {
              return;
            }

            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              const availableOptions = Array.from(options).filter(
                (option) => Number(option.dataset.count) > 0,
              );
              select.append(...availableOptions);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });
        })
        .catch((error) => {
          throw error;
        });
    }

    populateCollectionFilters(background = false, targetPanel = null) {
      const panel = targetPanel || this.activePanel;
      const selects = panel ? panel.querySelectorAll("select.dropdown-filter") : this.querySelectorAll("select.dropdown-filter");

      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");

      // When we initially populate the filters we don't want
      // to show a loading spinner.
      if (!background) {
        this.toggleLoading();
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (!background) {
            this.toggleLoading();
          }

          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          selects.forEach((select) => {
            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              select.append(...options);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });

          if (panel) {
            panel.dataset.populated = "true";
          }

          this.setFormUrl(this.constructURL());
        })
        .catch((error) => {
          throw error;
        });
    }

    
    onCollectionChange() {
      this.panels.forEach((panel) => {
        panel.dataset.populated = "false";
      });

      this.querySelectorAll("select.dropdown-filter").forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.populateCollectionFilters();
    }

    toggleLoading() {
      this.classList.toggle("is-loading");
      this.button.classList.toggle("loading");
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
